import * as React from "react";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { Table } from "../../newcomponents/table/table";
import { TableNewItemButton } from "../../newcomponents/table/components/tableNewButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { WorkloadColumnGroup } from "../../newcomponents/table/columnGroups/workload";
import { useColumnsWithTagColumns } from "../../newcomponents/table/columns/tagColumns/useColumnsWithTagColumns";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { Workload } from "../../schema/types/workload/entity";
import { linksOf } from "../../services/cpln";
import {
  getWorkloadTableItem,
  parseReadiness,
  STORAGE_KEY_WORKLOAD_LIST_LIMIT_TO_CONTEXT_GVC,
  workloadHelpers,
  WorkloadTableItem,
} from "../../mst/kinds/workload.helpers";
import { TableActionsDropdown } from "../../newcomponents/table/components/tableActionsDropdown";
import { Checkbox, Modal, notification, Tag } from "antd";
import { updateLastDeploymentTimeOnHubspot } from "../../services/utils";
import { DeleteModal } from "../../components/modals/deleteModal";
import { Query } from "../../schema/types/query/query";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectGvc } from "../../store/contextSlice";
import { setHasWorkload } from "../../store/userDataSlice";
import { useGvcListUrlState } from "../../reactHooks/useGvcListUrlState";

interface WorkloadsStateSummary {
  notReady: number;
  suspended: number;
  ready: number;
}

const actionTextMap = {
  suspend: "suspend",
  unsuspend: "unsuspend",
  "force-redeployment": "force redeploy",
};

export const WorkloadList: React.FC = () => {
  const dispatch = useAppDispatch();
  const gvc = useAppSelector(selectGvc);
  const hasWorkload = useAppSelector((state) => state.userData.hasWorkload);

  const { limitToContextGVC, setLimitToContextGVC } = useGvcListUrlState({
    localStorageKey: STORAGE_KEY_WORKLOAD_LIST_LIMIT_TO_CONTEXT_GVC,
  });

  const limitToContextGVCQuery: Query = React.useMemo(
    () => ({
      kind: "workload",
      spec: {
        match: "all",
        terms: [
          {
            rel: "gvc",
            op: "=",
            value: gvc,
          },
        ],
      },
    }),
    [gvc],
  );

  // Determine the query to use based on limitToContextGVC and gvc
  const query = React.useMemo(() => {
    // If limitToContextGVC is enabled and there's a gvc context, use the GVC query
    if (limitToContextGVC && gvc) {
      return limitToContextGVCQuery;
    }
    // Otherwise, no query (fetch all workloads)
    return undefined;
  }, [limitToContextGVC, gvc, limitToContextGVCQuery]);

  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedLinks, setSelectedLinks] = React.useState<string[]>([]);
  const [action, setAction] = React.useState<string>("");

  const { kind, id } = useTableKindId("workload", "workload-list-update4");
  const qData = useTableItemQueryData<Workload>(kind, { query: query });
  const columns = useColumnsWithTagColumns(WorkloadColumnGroup, kind);

  // Show the actual state of the checkbox (whether it's limiting to context GVC)
  const processedLimitToContextGVC = React.useMemo(() => {
    // If there's no GVC context, can't limit to it
    if (!gvc) return false;
    // If there's a custom user query, respect that (don't show as limited to context)
    if (qData.query) return false;
    // Otherwise, show the limitToContextGVC state
    return limitToContextGVC;
  }, [gvc, qData.query, limitToContextGVC]);

  React.useEffect(() => {
    if (qData.items.length > 0 && !hasWorkload) {
      dispatch(setHasWorkload(true));
    }
  }, [qData.items.length]);

  function getWorkloadsStateSummary(): WorkloadsStateSummary {
    let notReady = 0;
    let suspended = 0;
    let ready = 0;

    for (const workload of qData.items) {
      if (workload.status?.suspendedStatus === "suspended") {
        suspended++;
        continue;
      }

      const r = parseReadiness(workload.health?.readiness);
      if (r === "latestReady" || r === "ready") {
        ready++;
        continue;
      }

      notReady++;
    }

    return { notReady, suspended, ready };
  }

  const { notReady, suspended, ready } = getWorkloadsStateSummary();
  const workloadTableItems: WorkloadTableItem[] = qData.visibleItems.map((workload) => {
    return getWorkloadTableItem({ item: workload });
  });

  return (
    <>
      <Table<WorkloadTableItem>
        title={(className) => {
          return (
            <div className="flex gap-2">
              <div className={className}>Workloads</div>
              <div className="flex items-center flex-wrap gap-1">
                {notReady > 0 ? <Tag color={`error`}>{`${notReady} Not Ready`}</Tag> : null}
                {suspended > 0 ? <Tag color={`warning`}>{`${suspended} Suspended`}</Tag> : null}
                {ready > 0 ? <Tag color={`success`}>{`${ready} Ready`}</Tag> : null}
              </div>
            </div>
          );
        }}
        data={workloadTableItems}
        selectKey="selfLink"
        selectMode="multi"
        selections={selectedLinks}
        onSelectionsChange={setSelectedLinks}
        tableId={id}
        headerRenderer={() => {
          return (
            <>
              <span data-guide="limit-by-context">
                <Checkbox
                  key={`${gvc || "all"}`}
                  disabled={!gvc || !!qData.query}
                  checked={processedLimitToContextGVC}
                  onChange={(e) => setLimitToContextGVC(e.target.checked)}
                >
                  Limit with Context GVC
                </Checkbox>
              </span>
              <TableNewItemButton kind={kind} />
              <TableActionsDropdown
                kind={kind}
                selectionLinks={selectedLinks}
                options={[
                  {
                    key: "suspend",
                    group: "Suspension",
                    label: "Suspend",
                    disabled: selectedLinks.length < 1,
                    callback: () => setAction("suspend"),
                  },
                  {
                    key: "unsuspend",
                    group: "Suspension",
                    label: "Unsuspend",
                    disabled: selectedLinks.length < 1,
                    callback: () => setAction("unsuspend"),
                  },
                  {
                    key: "force-redeployment",
                    label: "Force Redeployment",
                    disabled: selectedLinks.length < 1,
                    callback: () => setAction("force-redeployment"),
                  },
                  {
                    key: "delete",
                    label: "Delete",
                    disabled: selectedLinks.length < 1,
                    danger: true,
                    callback: () => setAction("delete"),
                  },
                ]}
              />
              <TableHeaderRefreshButton onClick={qData.refetch} />
              <CustomTableCPLNQuery
                tableId={id}
                kind={kind}
                onQuery={qData.setCplnQuery}
                isLoading={qData.isLoading}
              />
            </>
          );
        }}
        enableFilter
        isLoading={qData.isLoading}
        onSortChange={qData.setSort}
        onSearchChange={qData.setSearch}
        isLoadingMore={qData.isLoadingMore}
        hasMore={qData.hasMore}
        onLoadMore={qData.fetchNextPage}
        columns={columns}
      />
      {["suspend", "unsuspend"].includes(action as any) ? (
        <Modal
          title={`Are you sure you want to ${actionTextMap[action as any]} selected workloads?`}
          width={620}
          open={true}
          onCancel={() => setAction("")}
          onOk={async () => {
            setIsLoading(true);
            let result: true | string = true;
            if (action === "suspend") {
              result = await workloadHelpers.handleSuspend(selectedLinks);
            } else if (action === "unsuspend") {
              result = await workloadHelpers.handleUnsuspend(selectedLinks);
            }

            if (result === true) {
              notification.success({
                message: "Success",
              });
              setIsLoading(false);
              setAction("");
              setSelectedLinks([]);
            } else {
              setIsLoading(false);
              setAction("");
              notification.warning({
                message: "Failed",
                description: result,
              });
            }
          }}
          cancelButtonProps={{ disabled: isLoading }}
          okButtonProps={{ disabled: isLoading, loading: isLoading }}
        >
          <div>
            <p>
              Adjusting the suspend status of workloads will automatically update the "suspend" property in both the
              default and local options within the workload specification.
            </p>
          </div>
        </Modal>
      ) : null}
      {action === "force-redeployment" ? (
        <Modal
          title={"Force Redeployment"}
          open={true}
          onCancel={() => setAction("")}
          onOk={async () => {
            setIsLoading(true);
            const result = await workloadHelpers.handleForceRedeployment(selectedLinks);
            if (result === true) {
              notification.success({
                message: "Success",
                description: "Redeployment initiated",
              });
              updateLastDeploymentTimeOnHubspot();
              setIsLoading(false);
              setAction("");
              setSelectedLinks([]);
            } else {
              setIsLoading(false);
              setAction("");
              notification.warning({
                message: "Failed",
                description: result,
              });
            }
          }}
          cancelButtonProps={{ disabled: isLoading }}
          okButtonProps={{ disabled: isLoading, loading: isLoading }}
        >
          <div>Are you sure you want to force redeployment for selected workloads?</div>
        </Modal>
      ) : null}
      {action === "delete" ? (
        <DeleteModal
          kind={kind}
          onCancel={() => setAction("")}
          onComplete={() => {
            setAction("");
            setSelectedLinks([]);
            qData.refetch();
          }}
          links={selectedLinks}
        />
      ) : null}
    </>
  );
};
